import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { API } from '../config/api';
import {
  Sparkles, DollarSign, TrendingUp, Eye, MousePointer,
  Loader2, Star, Music, Video, Mic, Radio, Headphones,
  Building2, Clock, CheckCircle, AlertCircle, ArrowRight
} from 'lucide-react';

const SLOT_ICONS = {
  soundstage_spotlight_paid: Sparkles,
  featured_artist_week: Star,
  top_song_week: Music,
  top_video_week: Video,
  artist_on_rise_week: TrendingUp,
  top_podcaster_week: Mic,
  top_streamer_week: Radio,
  top_producer_week: Headphones,
  top_studio_week: Building2,
};

const SLOT_COLORS = {
  soundstage_spotlight_paid: 'border-yellow-500/40 bg-yellow-500/5 hover:bg-yellow-500/10',
  featured_artist_week: 'border-purple-500/40 bg-purple-500/5 hover:bg-purple-500/10',
  top_song_week: 'border-orange-500/40 bg-orange-500/5 hover:bg-orange-500/10',
  top_video_week: 'border-red-500/40 bg-red-500/5 hover:bg-red-500/10',
  artist_on_rise_week: 'border-emerald-500/40 bg-emerald-500/5 hover:bg-emerald-500/10',
  top_podcaster_week: 'border-blue-500/40 bg-blue-500/5 hover:bg-blue-500/10',
  top_streamer_week: 'border-pink-500/40 bg-pink-500/5 hover:bg-pink-500/10',
  top_producer_week: 'border-violet-500/40 bg-violet-500/5 hover:bg-violet-500/10',
  top_studio_week: 'border-amber-500/40 bg-amber-500/5 hover:bg-amber-500/10',
};

const STATUS_STYLES = {
  active: { color: 'bg-green-500/20 text-green-400', label: 'Active' },
  pending_payment: { color: 'bg-amber-500/20 text-amber-400', label: 'Pending Payment' },
  expired: { color: 'bg-muted/40 text-muted-foreground', label: 'Expired' },
  completed: { color: 'bg-blue-500/20 text-blue-400', label: 'Completed' },
};

export default function PromotionsPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user, getAuthHeaders, loading: authLoading } = useAuth();
  const [pricing, setPricing] = useState([]);
  const [myPromos, setMyPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [activeTab, setActiveTab] = useState('browse');
  const [targetId, setTargetId] = useState('');
  const [targetType, setTargetType] = useState('user');

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { navigate('/login'); return; }
    fetchData();
  }, [isAuthenticated, authLoading]);

  const fetchData = async () => {
    setLoading(true);
    const headers = getAuthHeaders();
    try {
      const [pricingRes, myRes] = await Promise.all([
        fetch(`${API}/promotions/pricing`),
        fetch(`${API}/promotions/my`, { headers }),
      ]);
      if (pricingRes.ok) setPricing((await pricingRes.json()).slots || []);
      if (myRes.ok) setMyPromos((await myRes.json()).promotions || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handlePurchase = async (slotKey) => {
    const tid = targetId.trim() || user?.user_id;
    if (!tid) { toast.error('Please enter a target ID'); return; }

    setPurchasing(slotKey);
    try {
      const res = await fetch(`${API}/promotions/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ slot_key: slotKey, target_id: tid, target_type: targetType }),
      });
      if (res.ok) {
        const d = await res.json();
        if (d.checkout_url) {
          toast.success('Redirecting to Stripe checkout...');
          window.location.href = d.checkout_url;
          return;
        }
        toast.success(d.message || 'Promotion reserved!');
        fetchData();
        setActiveTab('my-promotions');
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Purchase failed');
      }
    } catch { toast.error('Network error'); }
    setPurchasing(null);
  };

  if (authLoading || loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }

  return (
    <div className="min-h-screen py-8 px-4" data-testid="promotions-page">
      <div className="max-w-5xl mx-auto">
        <div className="mb-8">
          <Badge className="mb-3 bg-yellow-500/20 text-yellow-400"><Sparkles className="w-3 h-3 mr-1" />Featured Promotions</Badge>
          <h1 className="font-heading text-3xl font-bold">Promote Your Content</h1>
          <p className="text-muted-foreground mt-2">Get featured on the DMV Soundstage homepage. Paid placements override organic algorithm rankings.</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-muted/30 p-1 mb-6">
            <TabsTrigger value="browse" data-testid="promo-tab-browse">
              <Sparkles className="w-4 h-4 mr-2" />Browse Slots
            </TabsTrigger>
            <TabsTrigger value="my-promotions" data-testid="promo-tab-my">
              <TrendingUp className="w-4 h-4 mr-2" />My Promotions
              {myPromos.length > 0 && <Badge variant="secondary" className="ml-2 text-xs">{myPromos.length}</Badge>}
            </TabsTrigger>
          </TabsList>

          {/* Browse Slots */}
          <TabsContent value="browse">
            {/* Target selector */}
            <Card className="glass-card border-border p-5 mb-6">
              <h3 className="font-heading text-lg font-bold mb-3">What do you want to promote?</h3>
              <div className="flex flex-col sm:flex-row gap-3">
                <Select value={targetType} onValueChange={setTargetType}>
                  <SelectTrigger className="w-40" data-testid="target-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">My Profile</SelectItem>
                    <SelectItem value="track">A Track</SelectItem>
                    <SelectItem value="video">A Video</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  placeholder={targetType === 'user' ? `Your ID (default: ${user?.user_id})` : `Enter ${targetType} ID`}
                  value={targetId}
                  onChange={e => setTargetId(e.target.value)}
                  className="flex-1"
                  data-testid="target-id-input"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-2">Leave blank to promote your own profile.</p>
            </Card>

            {/* Soundstage Spotlight (premium) */}
            {pricing.filter(p => p.slot_key === 'soundstage_spotlight_paid').map(slot => {
              const Icon = SLOT_ICONS[slot.slot_key] || Sparkles;
              return (
                <Card key={slot.slot_key} className="glass-card border-yellow-500/30 bg-gradient-to-r from-yellow-500/10 to-amber-500/5 p-6 mb-6" data-testid={`promo-slot-${slot.slot_key}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 rounded-xl bg-yellow-500/20 flex items-center justify-center"><Icon className="w-7 h-7 text-yellow-400" /></div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-heading text-xl font-bold">{slot.label}</h3>
                          <Badge className="bg-yellow-500/20 text-yellow-400">Premium</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">The #1 paid slot. Maximum visibility. Shown at the top of all featured content across the platform.</p>
                        <div className="flex items-center gap-4 mt-2 text-sm">
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{slot.duration_days} days</span>
                          <span className="flex items-center gap-1"><TrendingUp className="w-3 h-3" />Priority: {slot.priority}</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-heading text-2xl font-bold text-yellow-400">{slot.price_display}</p>
                      <Button className="mt-2 bg-yellow-500 hover:bg-yellow-600 text-black" onClick={() => handlePurchase(slot.slot_key)} disabled={purchasing === slot.slot_key} data-testid={`buy-${slot.slot_key}`}>
                        {purchasing === slot.slot_key ? <Loader2 className="w-4 h-4 animate-spin" /> : <><DollarSign className="w-4 h-4 mr-1" />Get Featured</>}
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}

            {/* Other slots */}
            <div className="grid gap-4 sm:grid-cols-2">
              {pricing.filter(p => p.slot_key !== 'soundstage_spotlight_paid').map(slot => {
                const Icon = SLOT_ICONS[slot.slot_key] || Star;
                const colorCls = SLOT_COLORS[slot.slot_key] || 'border-border';
                return (
                  <Card key={slot.slot_key} className={`glass-card ${colorCls} p-5 transition-colors`} data-testid={`promo-slot-${slot.slot_key}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-muted/30 flex items-center justify-center"><Icon className="w-5 h-5" /></div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-heading font-bold">{slot.label}</h4>
                        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                          <span><Clock className="w-3 h-3 inline mr-1" />{slot.duration_days} days</span>
                          <span>Priority: {slot.priority}</span>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="font-bold text-lg">{slot.price_display}</p>
                        <Button size="sm" variant="outline" className="mt-1" onClick={() => handlePurchase(slot.slot_key)} disabled={purchasing === slot.slot_key} data-testid={`buy-${slot.slot_key}`}>
                          {purchasing === slot.slot_key ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Buy'}
                        </Button>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* My Promotions */}
          <TabsContent value="my-promotions">
            {myPromos.length === 0 ? (
              <Card className="glass-card border-border p-12 text-center">
                <Sparkles className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-medium text-lg mb-2">No Promotions Yet</h3>
                <p className="text-muted-foreground mb-4">Purchase a featured slot to promote your content.</p>
                <Button onClick={() => setActiveTab('browse')} data-testid="browse-promos-cta"><ArrowRight className="w-4 h-4 mr-2" />Browse Slots</Button>
              </Card>
            ) : (
              <div className="space-y-4">
                {myPromos.map(p => {
                  const Icon = SLOT_ICONS[p.slot_key] || Sparkles;
                  const st = STATUS_STYLES[p.status] || STATUS_STYLES.expired;
                  const analytics = p.analytics || {};
                  return (
                    <Card key={p.promotion_id} className="glass-card border-border p-5" data-testid={`my-promo-${p.promotion_id}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-muted/30 flex items-center justify-center"><Icon className="w-5 h-5" /></div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-medium">{PROMO_PRICING_LABELS[p.slot_key] || p.slot_key}</h4>
                              <Badge className={st.color}>{st.label}</Badge>
                            </div>
                            <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                              <span>Target: {p.target_id}</span>
                              <span>Paid: ${(p.amount_paid / 100).toFixed(2)}</span>
                            </div>
                            <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                              <span>Start: {new Date(p.start_at).toLocaleDateString()}</span>
                              <span>End: {new Date(p.end_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-center">
                            <p className="font-bold text-lg">{analytics.impressions || 0}</p>
                            <p className="text-xs text-muted-foreground flex items-center gap-1"><Eye className="w-3 h-3" />Views</p>
                          </div>
                          <div className="text-center">
                            <p className="font-bold text-lg">{analytics.clicks || 0}</p>
                            <p className="text-xs text-muted-foreground flex items-center gap-1"><MousePointer className="w-3 h-3" />Clicks</p>
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

const PROMO_PRICING_LABELS = {
  soundstage_spotlight_paid: 'Soundstage Spotlight',
  featured_artist_week: 'Featured Artist',
  top_song_week: 'Top Song Placement',
  top_video_week: 'Top Video Placement',
  artist_on_rise_week: 'Artist on the Rise',
  top_podcaster_week: 'Top Podcaster',
  top_streamer_week: 'Top Streamer',
  top_producer_week: 'Top Producer',
  top_studio_week: 'Top Studio',
};
